import React, { useEffect, useState, useRef, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from '@emotion/react';
import { darkTheme, lightTheme } from '../themes';
import { DateTime } from 'luxon';
import { formatNumber } from '@/utils/formatters';
import useResponsiveChartHeight from '@/components/highcharts/powerAnalyser/hooks/useResponsiveChartHeight';

const getWeekNumber = (date) => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7; // Make Monday=0
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  const firstWeekDay = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - firstWeekDay + 3);
  const firstThursdayOfYear = target.valueOf();

  return (
    1 +
    Math.round((firstThursday - firstThursdayOfYear) / (7 * 24 * 3600 * 1000))
  );
};

const hasDSTTransition = (timestamps, timeZone = 'Europe/Copenhagen') => {
  for (let i = 1; i < timestamps.length; i++) {
    const prev = DateTime.fromMillis(timestamps[i - 1], { zone: timeZone });
    const curr = DateTime.fromMillis(timestamps[i], { zone: timeZone });

    if (prev.offset !== curr.offset) {
      return { hasDST: true, index: i };
    }
  }
  return { hasDST: false };
};

const getYear = (date) => {
  return new Date(date).getFullYear();
};

const formatTimestamp = (timestamp, aggregate) => {
  const date = new Date(timestamp);

  switch (aggregate) {
    case 'hour':
      return date.toLocaleString('da-DK', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // Use 24-hour format
      });

    case 'day':
      return date.toLocaleDateString('da-DK', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });

    case 'week': {
      const weekNumber = getWeekNumber(date);
      const year = getYear(date);
      return `uge ${weekNumber} ${year}`;
    }

    case 'month':
      return date.toLocaleDateString('da-DK', {
        month: 'short',
      });

    default:
      return date.toLocaleString('da-DK');
  }
};

const normalizeByIndex = (timestamps) => {
  return timestamps.map((_, index) => index); // [0, 1, 2, ...]
};

const EnergyAnalysisComparisonSeriesChart = ({
  energyConsumptionData,
  dataSeriesAggregate,
  isLoading,
  node,
  viewMode,
  aggregate,
  selectedEnergyGroup,
  comparisonEnergyConsumptionData,
  comparisonTimeRange,
  comparisonItems,
  timeRange,
}) => {
  const chartComponentRef = useRef(null);
  const [chartOptions, setChartOptions] = useState({});
  const theme = useTheme();
  const chartHeight = useResponsiveChartHeight();

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);

  const comparisonDataMap = useMemo(() => {
    const map = new Map();

    comparisonEnergyConsumptionData.forEach((item) => {
      map.set(item?.EnergySummary.Id, item?.EnergyData || []);
    });

    return map;
  }, [comparisonEnergyConsumptionData]);

  const normalizedPeriods = useMemo(() => {
    if (
      !timeRange ||
      !comparisonTimeRange ||
      !energyConsumptionData ||
      !comparisonDataMap ||
      !comparisonEnergyConsumptionData
    )
      return null;

    const globalTimestamps = [
      ...new Set(
        (energyConsumptionData?.EnergyData || []).map((d) =>
          Date.parse(d.timestamp),
        ),
      ),
    ].sort((a, b) => a - b);

    const comparisonTimestamps = [
      ...new Set(
        comparisonItems
          .filter((item) => item.node)
          .flatMap(
            (item) =>
              comparisonDataMap
                .get(item.node.energyTreeNodeId)
                ?.map((d) => Date.parse(d.timestamp)) || [],
          ),
      ),
    ].sort((a, b) => a - b);

    return {
      globalTimestamps,
      comparisonTimestamps,
      normalizedGlobalPeriods: normalizeByIndex(globalTimestamps),
      normalizedComparisonPeriods: normalizeByIndex(comparisonTimestamps),
    };
  }, [
    energyConsumptionData,
    comparisonEnergyConsumptionData,
    comparisonDataMap,
    comparisonItems,
    timeRange,
    comparisonTimeRange,
    aggregate,
    viewMode,
  ]);

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) chart.showLoading('Loading data...');
      else chart.hideLoading();
    }

    const {
      globalTimestamps,
      comparisonTimestamps,
      normalizedGlobalPeriods,
      normalizedComparisonPeriods,
    } = normalizedPeriods;

    const { hasDST: mainHasDST, index: mainDSTIndex } =
      hasDSTTransition(globalTimestamps);
    const { hasDST: compHasDST, index: compDSTIndex } =
      hasDSTTransition(comparisonTimestamps);

    const showDSTMarker = viewMode === 'line' && (mainHasDST || compHasDST);
    const dstIndex = mainHasDST
      ? normalizedGlobalPeriods[mainDSTIndex]
      : compDSTIndex
      ? normalizedComparisonPeriods[compDSTIndex]
      : null;

    const timestampLookup = new Map();
    globalTimestamps.forEach((timestamp, index) => {
      timestampLookup.set(normalizedGlobalPeriods[index], {
        actualTimestamp: timestamp,
        formattedDate: formatTimestamp(timestamp, aggregate),
      });
    });

    const comparisonTimestampLookup = new Map();

    comparisonTimestamps.forEach((timestamp, index) => {
      comparisonTimestampLookup.set(normalizedComparisonPeriods[index], {
        actualTimestamp: timestamp,
        formattedDate: formatTimestamp(timestamp, aggregate),
      });
    });

    const chartSeries = [];

    // ✅ Build Global Series (Normalized Periods)
    chartSeries.push({
      name: `${node?.name} (Valgte målepunkt)`,
      data: globalTimestamps.map((timestamp, index) => {
        const point = energyConsumptionData?.EnergyData.find(
          (d) => Date.parse(d.timestamp) === timestamp,
        );
        return {
          x: normalizedGlobalPeriods[index], // ✅ Use normalized periods
          y: point?.[dataSeriesAggregate?.value] || null,
          actualTimestamp: timestamp, // ✅ Preserve actual timestamp
        };
      }),
      color: 'hsla(67, 100%, 40%, 1)',
      type: viewMode === 'line' ? 'line' : 'column',
    });

    comparisonItems
      .filter((item) => item.node)
      .forEach((item) => {
        const energyData = comparisonDataMap.get(item.node.energyTreeNodeId);
        chartSeries.push({
          name: `${item.node.name} (Sammenligning)`,
          data: comparisonTimestamps.map((timestamp, index) => {
            const point = energyData?.find(
              (d) => Date.parse(d.timestamp) === timestamp,
            );
            return {
              x: normalizedComparisonPeriods[index],
              y: point?.[dataSeriesAggregate?.value] || null,
              actualTimestamp: timestamp,
            };
          }),
          type: viewMode === 'line' ? 'line' : 'column',
        });
      });

    const delay = 1; // Small delay to reduce rapid flickers
    const timeout = setTimeout(() => {
      // ----- Final Chart Options -----
      setChartOptions({
        chart: {
          type: viewMode === 'line' ? 'line' : 'column',
          height: chartHeight,
          zooming: { type: 'x', mouseWheel: { enabled: false } },
          spacingLeft: 50, // ✅ Adds left padding
          spacingRight: 50, // ✅ Adds right padding
        },
        credits: {
          enabled: false,
        },
        time: { useUTC: true },
        title: { text: '' },
        xAxis: {
          type: 'linear', // Because x values are 0, 1, 2, 3... (NOT timestamps)
          crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
          plotLines:
            showDSTMarker && dstIndex != null
              ? [
                  {
                    color: 'red',
                    dashStyle: 'shortdash',
                    width: 2,
                    value: dstIndex, // Now correct: this is the normalized index
                    zIndex: 5,
                    label: {
                      text: '⏰ DST skift',
                      verticalAlign: 'top',
                      y: -10,
                    },
                  },
                ]
              : [],
          labels: {
            useHTML: true,
            formatter: function () {
              const normalizedPeriod = this.value;
              const originalDateObj = timestampLookup.get(normalizedPeriod);
              const comparisonDateObj =
                comparisonTimestampLookup.get(normalizedPeriod);

              if (!originalDateObj || !comparisonDateObj)
                return originalDateObj?.formattedDate || '';

              if (
                originalDateObj.formattedDate ===
                comparisonDateObj.formattedDate
              ) {
                return `<div style="text-align:center;">
                          <span style="font-size:12px;">${originalDateObj.formattedDate}</span>
                        </div>`;
              }

              return `<div style="text-align:center;">
                <span style="font-size:12px;">${originalDateObj?.formattedDate}</span><br/>
                <span style="font-size:12px;">${comparisonDateObj.formattedDate}</span>
              </div>`;
            },
            style: {
              whiteSpace: 'normal',
              width: '30px',
              textAlign: 'center',
            },
          },
        },
        yAxis: {
          min: 0,
          title: { text: 'Energiforbrug (kWh)' },
          crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        },
        tooltip: {
          shared: true,
          useHTML: true,
          formatter: function () {
            let tooltip = ``;

            this.points.forEach((point) => {
              const actualTime = formatTimestamp(
                point.point.actualTimestamp,
                aggregate,
              );
              tooltip += `
                <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${
                  point.series.color
                };margin-right:5px;"></span>
                ${point.series.name}: <b>${formatNumber(point.y)} kWh</b>
                <span style="font-size:12px;color:gray;">(${actualTime})</span><br/>
              `;
            });

            return tooltip;
          },
        },
        plotOptions: {
          line: { marker: { enabled: false } },
          column: { grouping: true, borderColor: 'transparent' },
          connectNulls: false,
        },
        series: chartSeries,
      });
    }, delay);

    return () => clearTimeout(timeout); // Cleanup if dependencies change quickly
  }, [
    comparisonItems,
    comparisonEnergyConsumptionData,
    comparisonTimeRange,
    comparisonDataMap,
    dataSeriesAggregate,
    normalizedPeriods,
    timeRange,
    energyConsumptionData,
    viewMode,
    aggregate,
    isLoading,
    selectedEnergyGroup,
  ]);

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
            type: 'datetime',
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);

  return (
    <>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartComponentRef}
      />
    </>
  );
};

export default EnergyAnalysisComparisonSeriesChart;
